import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/store';
import { useRefreshMutation } from '@/store/api/authApi';
import { setTokens, logout } from '@/store/slices/authSlice';
import { tokenFromStorage, withRefreshLock } from '@/store/session-lock';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPermission?: string;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredPermission,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { isAuthenticated, user, refreshToken } = useAppSelector((state) => state.auth);
  const [erneuern] = useRefreshMutation();

  /**
   * Das Zugangstoken lebt fünfzehn Minuten, das Erneuerungstoken sieben Tage.
   * Wer nach einer halben Stunde die Seite neu lädt, hat also kein gültiges
   * Zugangstoken mehr — aber eine gültige Sitzung. Vorher landete er trotzdem
   * auf der Anmeldeseite: Die Oberfläche sah nur das abgelaufene Token und
   * schickte ihn weg, ohne das zweite auch nur zu versuchen.
   *
   * Jetzt wird einmal erneuert, bevor entschieden wird. Genau einmal: Scheitert
   * es, ist die Sitzung wirklich vorbei.
   */
  const [erneuerung, setErneuerung] = useState<'offen' | 'laeuft' | 'gescheitert'>(
    !isAuthenticated && refreshToken ? 'offen' : 'gescheitert',
  );

  useEffect(() => {
    if (isAuthenticated || erneuerung !== 'offen' || !refreshToken) return;
    setErneuerung('laeuft');
    // Unter derselben Sperre wie jede andere Erneuerung: Werden zwei Reiter
    // gleichzeitig neu geladen, dürfte sonst jeder seine eigene Kopie des
    // Erneuerungstokens vorlegen — und die zweite Vorlage beendet alle
    // Sitzungen. Wer die Sperre hält, sieht zuerst in den Speicher.
    withRefreshLock(async () => {
      const imSpeicher = tokenFromStorage('refreshToken');
      if (imSpeicher && imSpeicher !== refreshToken) {
        dispatch(
          setTokens({ accessToken: tokenFromStorage('accessToken') ?? '', refreshToken: imSpeicher }),
        );
        return;
      }
      const token = await erneuern({ refreshToken: imSpeicher ?? refreshToken }).unwrap();
      dispatch(setTokens(token));
    })
      .catch(() => dispatch(logout()))
      .finally(() => setErneuerung('gescheitert'));
  }, [isAuthenticated, erneuerung, refreshToken, erneuern, dispatch]);

  if (!isAuthenticated) {
    if (refreshToken && erneuerung !== 'gescheitert') {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      );
    }
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (
    requiredPermission &&
    user &&
    !user.permissions.includes(requiredPermission)
  ) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          textAlign: 'center',
          p: 4,
        }}
      >
        <LockIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          {t('errors.unauthorized')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {t('errors.forbidden')}
        </Typography>
        <Button variant="contained" href="/">
          {t('nav.dashboard')}
        </Button>
      </Box>
    );
  }

  return <>{children}</>;
};
